import katex from 'katex';

/**
 * Minimal, XSS-safe markdown -> HTML for the chat assistant.
 *
 * The assistant replies in basic markdown (headings, bold, italic, code, links,
 * bullet lists, GFM pipe tables, and LaTeX math). We render a small, fixed subset.
 * Safety model: prose is HTML-escaped before formatting; formulas are rendered by
 * KaTeX with trusted commands disabled. Nothing the model emits can inject raw
 * markup. ATX headings (#..######) render as a styled bold <p> (kept compact for
 * chat bubbles). A line of 3+ dashes/asterisks/underscores becomes an <hr>.
 * ```fenced``` blocks render verbatim inside <pre><code> (no inline formatting).
 * Links are restricted to http(s). Math accepts \(...\) or $...$ inline and
 * \[...\] or $$...$$ in display mode.
 *
 * Use with `dangerouslySetInnerHTML={{ __html: mdToHtml(text) }}` — safe because
 * the output is built only from escaped text + the fixed tag set above.
 */

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

// Formula text reaches here after the containing prose has been escaped. Decode
// only the five entities introduced by escapeHtml, then let KaTeX perform its own
// escaping. This preserves operators such as < and alignment characters such as &.
function unescapeFormula(s: string): string {
  return s
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&amp;/g, '&');
}

function renderMath(expression: string, displayMode: boolean): string {
  return katex.renderToString(unescapeFormula(expression).trim(), {
    displayMode,
    throwOnError: false,
    strict: 'ignore',
    trust: false,
  });
}

const MATH_SENTINEL = String.fromCharCode(1);

function mathPlaceholder(index: number): string {
  return `${MATH_SENTINEL}${index}${MATH_SENTINEL}`;
}

/**
 * Protect display formulas before splitting the source into lines. This lets a
 * $$...$$ or \[...\] block span multiple lines without its KaTeX markup being
 * interpreted as Markdown.
 */
function protectDisplayMath(src: string): { source: string; rendered: string[] } {
  const rendered: string[] = [];
  const protect = (_match: string, expression: string) => {
    if (!expression.trim()) return _match;
    rendered.push(renderMath(expression, true));
    return mathPlaceholder(rendered.length - 1);
  };

  const protectSegment = (segment: string) => segment
    .replace(/\\\[([\s\S]*?)\\\]/g, protect)
    .replace(/\$\$([\s\S]*?)\$\$/g, protect);

  // Fenced code is deliberately excluded: formula-looking documentation and
  // examples inside ``` blocks must stay verbatim.
  let source = '';
  let prose = '';
  let inFence = false;
  const lines = src.match(/[^\n]*(?:\n|$)/g) ?? [];
  for (const line of lines) {
    if (/^\s*```/.test(line)) {
      if (!inFence) {
        source += protectSegment(prose);
        prose = '';
      }
      source += line;
      inFence = !inFence;
    } else if (inFence) {
      source += line;
    } else {
      prose += line;
    }
  }
  source += protectSegment(prose);

  return {
    source,
    rendered,
  };
}

function isDollarMath(expression: string): boolean {
  // Whitespace immediately inside $ delimiters is normally prose/currency, not
  // Markdown math. A leading digit is also a strong currency signal in assistant
  // prose: without this guard, "$20k ... with ~$7k" is treated as one formula
  // ending at the second amount's dollar sign, and KaTeX collapses all of the
  // spaces in between. Authors can use the unambiguous \(...\) delimiters for
  // formulas that start with a number.
  return expression.trim() === expression
    && expression.length > 0
    && !/^\d/.test(expression)
    && !/^[\d\s,.+\-–—]+$/.test(expression);
}

// Inline formatting on already-escaped text.
function inline(s: string, displayMath: string[] = []): string {
  // Mask code spans first so their contents aren't further formatted. The NUL
  // sentinel can't occur in the escaped, single-line input, so it won't collide.
  const NUL = String.fromCharCode(0);
  const codes: string[] = [];
  s = s.replace(/`([^`]+)`/g, (_m, c) => {
    codes.push(c);
    return `${NUL}${codes.length - 1}${NUL}`;
  });
  // Mask formulas before Markdown's underscore/asterisk rules run. Otherwise
  // subscripts such as V_{OC} would be mistaken for emphasis.
  const maths = [...displayMath];
  s = s.replace(/\\\((.+?)\\\)/g, (_match, expression) => {
    maths.push(renderMath(expression, false));
    return mathPlaceholder(maths.length - 1);
  });
  s = s.replace(/(^|[^\\$])\$(?!\$)([^\n$]+?)(?<!\\)\$/g, (match, prefix, expression) => {
    if (!isDollarMath(expression)) return match;
    maths.push(renderMath(expression, false));
    return `${prefix}${mathPlaceholder(maths.length - 1)}`;
  });
  // Links: [text](http(s)://...) only.
  s = s.replace(
    /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g,
    (_m, text, url) => `<a href="${url}" target="_blank" rel="noopener noreferrer">${text}</a>`,
  );
  // Bold, then italic (bold first so ** isn't eaten by the single-* rule).
  s = s
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
    .replace(/__([^_]+)__/g, '<strong>$1</strong>')
    .replace(/(^|[^*])\*([^*\s][^*]*?)\*/g, '$1<em>$2</em>')
    .replace(/(^|[^_])_([^_\s][^_]*?)_/g, '$1<em>$2</em>');
  // Restore code spans.
  s = s.replace(new RegExp(`${NUL}(\\d+)${NUL}`, 'g'), (_m, i) => `<code>${codes[Number(i)]}</code>`);
  // Restore both pre-rendered display formulas and inline formulas last, after
  // all Markdown substitutions have finished.
  s = s.replace(new RegExp(`${MATH_SENTINEL}(\\d+)${MATH_SENTINEL}`, 'g'), (_m, i) => maths[Number(i)]);
  return s;
}

// GFM pipe tables. Split a row into trimmed cells (outer pipes optional).
function tableCells(line: string): string[] {
  return line.trim().replace(/^\||\|$/g, '').split('|').map((c) => c.trim());
}

// A separator row like `| --- | :--: |` — every cell is dashes with optional colons.
function isTableSeparator(line: string): boolean {
  const t = line.trim();
  if (!t.includes('-') || !t.includes('|')) return false;
  return tableCells(t).every((c) => /^:?-+:?$/.test(c));
}

const TH = 'border border-slate-300 dark:border-slate-600 px-2 py-1 text-left font-semibold bg-slate-50 dark:bg-slate-800';
const TD = 'border border-slate-300 dark:border-slate-600 px-2 py-1';

function renderTable(lines: string[], start: number, displayMath: string[]): { html: string; end: number } {
  const header = tableCells(lines[start]);
  let i = start + 2; // skip header + separator
  const rows: string[][] = [];
  for (; i < lines.length; i++) {
    const t = lines[i].trim();
    if (t === '' || !t.includes('|')) break;
    rows.push(tableCells(lines[i]));
  }
  const thead = `<thead><tr>${header.map((h) => `<th class="${TH}">${inline(h, displayMath)}</th>`).join('')}</tr></thead>`;
  const tbody = `<tbody>${rows.map((r) => `<tr>${r.map((c) => `<td class="${TD}">${inline(c, displayMath)}</td>`).join('')}</tr>`).join('')}</tbody>`;
  return { html: `<div class="overflow-x-auto my-2"><table class="text-xs border-collapse">${thead}${tbody}</table></div>`, end: i - 1 };
}

export function mdToHtml(src: string): string {
  const protectedMath = protectDisplayMath(src ?? '');
  const lines = escapeHtml(protectedMath.source).split('\n');
  const out: string[] = [];
  let list: string[] | null = null;
  const flush = () => {
    if (list) {
      out.push(`<ul class="list-disc pl-5 my-1 space-y-0.5">${list.join('')}</ul>`);
      list = null;
    }
  };
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].replace(/\s+$/, '');
    // Fenced code block: ``` (optionally ```lang). Everything up to the closing
    // fence is emitted verbatim (already HTML-escaped) inside <pre><code>, with no
    // inline formatting, so multi-line Python etc. keeps its newlines and indent.
    if (/^\s*```/.test(line)) {
      flush();
      const body: string[] = [];
      let i2 = i + 1;
      for (; i2 < lines.length; i2++) {
        if (/^\s*```\s*$/.test(lines[i2])) break;
        body.push(lines[i2]);
      }
      out.push(
        '<pre class="my-2 overflow-x-auto rounded-md bg-slate-100 dark:bg-slate-800 p-2 text-xs">' +
          `<code>${body.join('\n')}</code></pre>`,
      );
      i = i2; // skip body + closing fence (or to EOF if unterminated)
      continue;
    }
    // Table: a row with a pipe immediately followed by a separator row.
    if (line.includes('|') && i + 1 < lines.length && isTableSeparator(lines[i + 1])) {
      flush();
      const { html, end } = renderTable(lines, i, protectedMath.rendered);
      out.push(html);
      i = end;
      continue;
    }
    // ATX heading (#..######). Rendered as a styled bold paragraph, sized down
    // for chat bubbles, so '### Foo' reads as a heading rather than literal '###'.
    const heading = line.match(/^(#{1,6})\s+(.*\S)\s*#*\s*$/);
    if (heading) {
      flush();
      const size = heading[1].length <= 2 ? 'text-base' : 'text-sm';
      out.push(`<p class="font-semibold ${size} mt-2 mb-0.5">${inline(heading[2], protectedMath.rendered)}</p>`);
      continue;
    }
    // Thematic break: a line of 3+ dashes, asterisks, or underscores (no other
    // content). Must come before the bullet check so '---' isn't read as a list.
    if (/^\s*(?:-{3,}|\*{3,}|_{3,})\s*$/.test(line)) {
      flush();
      out.push('<hr class="my-2 border-slate-200 dark:border-slate-700"/>');
      continue;
    }
    const bullet = line.match(/^\s*[-*]\s+(.*)$/);
    if (bullet) {
      if (!list) list = [];
      list.push(`<li>${inline(bullet[1], protectedMath.rendered)}</li>`);
      continue;
    }
    flush();
    if (line.trim() === '') {
      out.push('<br/>');
    } else {
      out.push(`<p class="my-1">${inline(line, protectedMath.rendered)}</p>`);
    }
  }
  flush();
  return out.join('');
}
